import {AiImageAspectRatioOptions, AiImageFilterOptions, AiImageModelOption, AiImageStylesOptions, MediaTypePrompt, TextToImageGridErrorState} from './ai-image-panel.types';
import type {ContextualMenuGroup} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {
  ContextualMenuGroupType,
  ContextualMenuItemGraphicType,
  type ContextualMenuItemWithEmoji,
  type ContextualMenuItemWithIcon,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import type {AiPromptBox as AiPromptBoxType} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {AiModel} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {isUserPremium} from '@Libraries/user.library';

export const getAiModelFromAiImageModel = (model?: AiImageModelOption): AiModel => {
  switch (model) {
    case AiImageModelOption.DeepInfraFluxSchnell:
      return AiModel.Flux;
    case AiImageModelOption.StabilityAICore:
      return AiModel.Stability;
    case AiImageModelOption.StabilityAIUltra:
      return AiModel.StabilityUltra;
    case AiImageModelOption.Gemini2_5:
      return AiModel.NanoBanana;
    default:
      return AiModel.Auto;
  }
};

export const getDimensionsForAiImageSkeletonItem = (aspectRatio: AiImageAspectRatioOptions): Partial<GridSkeletonItemStorage> => {
  switch (aspectRatio) {
    case AiImageAspectRatioOptions.Portrait:
      return {width: 40};
    case AiImageAspectRatioOptions.LandScape:
      return {width: 122};
    case AiImageAspectRatioOptions.Square:
    default:
      return {width: 70};
  }
};

export const getAiSkeletonItem = (id: string): GridSkeletonItemStorage => {
  return {
    id: id,
    type: GRID_ITEM_TYPE.SKELETON,
    width: 180,
    height: 70,
    useFixedDimensions: false,
  };
};

export const getErrorMessage = (errorState: string): string => {
  switch (errorState) {
    case TextToImageGridErrorState.FLAGGED.toString():
      return window.i18next.t('pmwjs_search_prompt_violation_message');
    case TextToImageGridErrorState.VAGUE.toString():
      return window.i18next.t('pmwjs_vague_prompt_error');
    case TextToImageGridErrorState.UNSUPPORTED_LANGUAGE.toString():
      return window.i18next.t('pmwjs_search_prompt_unsupported_language_message');
    case TextToImageGridErrorState.GENERIC.toString():
      return window.i18next.t('pmwjs_unexpected_error_occurred');
    case TextToImageGridErrorState.NONE.toString():
    default:
      return '';
  }
};

export const getCreditCostForModel = (value: AiImageModelOption): number => {
  switch (value) {
    case AiImageModelOption.StabilityAIUltra:
      return 3;
    case AiImageModelOption.Gemini2_5:
      return 2;
    default:
      return 1;
  }
};

export const getPromptBoxOptions = (
  mediaTypePrompt: MediaTypePrompt,
  model: AiImageModelOption,
  style: AiImageStylesOptions,
  aspectRatio: AiImageAspectRatioOptions
): AiPromptBoxType => {
  return {
    placeholder: getPlaceHolderText(mediaTypePrompt),
    showInitialAnimation: false,
    contextualMenu: getContextualMenuGroups(model, style, aspectRatio, mediaTypePrompt),
    size: AiPromptBoxSize.SMALL,
    creditsPill: {
      visible: false,
      actionCost: 0,
      remainingCredits: 1,
      showPremiumIcon: !isUserPremium(),
    },
    input: {
      hideScrollbar: true
    },
    promptBoxState: PromptBoxState.IDLE,
    prompt: '',
  };
};

export const getPlaceHolderText = (mediaTypePrompt: MediaTypePrompt): string => {
  if (mediaTypePrompt === MediaTypePrompt.LOGO) {
    return window.i18next.t('pmwjs_describe_logo_for_ai_image_generation');
  }
  if (mediaTypePrompt === MediaTypePrompt.STICKER) {
    return window.i18next.t('pmwjs_describe_sticker_for_ai_image_generation');
  }
  if (mediaTypePrompt === MediaTypePrompt.TEXT_ART) {
    return window.i18next.t('pmwjs_describe_text_art_for_ai_image_generation');
  }
  return window.i18next.t('pmwjs_describe_image_for_ai_image_generation');
};

export const getContextualMenuGroups = (model: AiImageModelOption, style: AiImageStylesOptions, aspectRatio: AiImageAspectRatioOptions, mediaTypePrompt: MediaTypePrompt): ContextualMenuGroup[] => {
  return [
    {
      label: window.i18next.t('pmwjs_style'),
      key: AiImageFilterOptions.Styles,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: window.i18next.t('pmwjs_photographic'),
          value: AiImageStylesOptions.Photographic,
          active: style === AiImageStylesOptions.Photographic,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '📷',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_digital_art'),
          value: AiImageStylesOptions.DigitalArt,
          active: style === AiImageStylesOptions.DigitalArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '💻',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_three_d_model'),
          value: AiImageStylesOptions.Model3d,
          active: style === AiImageStylesOptions.Model3d,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '📦',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_cinematic'),
          value: AiImageStylesOptions.Cinematic,
          active: style === AiImageStylesOptions.Cinematic,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🎬',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_futuristic'),
          value: AiImageStylesOptions.NeonPunk,
          active: style === AiImageStylesOptions.NeonPunk,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🤖',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_line_art'),
          value: AiImageStylesOptions.LineArt,
          active: style === AiImageStylesOptions.LineArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '✏️',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_fantasy'),
          value: AiImageStylesOptions.FantasyArt,
          active: style === AiImageStylesOptions.FantasyArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🐉',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_anime'),
          value: AiImageStylesOptions.Anime,
          active: style === AiImageStylesOptions.Anime,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '👩‍🎤',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_pixel_art'),
          value: AiImageStylesOptions.PixelArt,
          active: style === AiImageStylesOptions.PixelArt,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '👾',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_vintage'),
          value: AiImageStylesOptions.AnalogFilm,
          active: style === AiImageStylesOptions.AnalogFilm,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '📻',
        } as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_comic'),
          value: AiImageStylesOptions.ComicBook,
          active: style === AiImageStylesOptions.ComicBook,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🦸🏻‍♂️',
        } as ContextualMenuItemWithEmoji,
      ],
    },
    {
      label: window.i18next.t('pmwjs_aspect_ratio'),
      key: AiImageFilterOptions.AspectRatio,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: window.i18next.t('pmwjs_square'),
          value: AiImageAspectRatioOptions.Square,
          active: aspectRatio === AiImageAspectRatioOptions.Square,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-square',
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_landscape'),
          value: AiImageAspectRatioOptions.LandScape,
          active: aspectRatio === AiImageAspectRatioOptions.LandScape,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-landscape',
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_portrait'),
          value: AiImageAspectRatioOptions.Portrait,
          active: aspectRatio === AiImageAspectRatioOptions.Portrait,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-portrait',
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        } as ContextualMenuItemWithIcon,
      ],
    },
    {
      label: window.i18next.t('pmwjs_ai_model'),
      key: AiImageFilterOptions.AiModel,
      type: ContextualMenuGroupType.CARDS,
      items: [
        {
          label: window.i18next.t('pmwjs_auto'),
          value: AiImageModelOption.Auto.toString(),
          active: model === AiImageModelOption.Auto,
          description: window.i18next.t('pmwjs_ai_model_auto_desc'),
          topRightIcon: 'icon-credits',
          credits: getCreditCostForModel(AiImageModelOption.Auto),
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        },
        {
          label: window.i18next.t('pmwjs_nano_banana'),
          value: AiImageModelOption.Gemini2_5.toString(),
          active: model === AiImageModelOption.Gemini2_5,
          description: window.i18next.t('pmwjs_ai_model_nano_banana_desc'),
          topRightIcon: 'icon-credits',
          credits: getCreditCostForModel(AiImageModelOption.Gemini2_5),
        },
        {
          label: window.i18next.t('pmwjs_stability'),
          value: AiImageModelOption.StabilityAICore.toString(),
          active: model === AiImageModelOption.StabilityAICore,
          description: window.i18next.t('pmwjs_ai_model_stability_desc'),
          topRightIcon: 'icon-credits',
          credits: getCreditCostForModel(AiImageModelOption.StabilityAICore),
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        },
        {
          label: window.i18next.t('pmwjs_stability_ultra'),
          value: AiImageModelOption.StabilityAIUltra.toString(),
          active: model === AiImageModelOption.StabilityAIUltra,
          description: window.i18next.t('pmwjs_ai_model_stability_ultra_desc'),
          topRightIcon: 'icon-credits',
          credits: getCreditCostForModel(AiImageModelOption.StabilityAIUltra),
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        },
        {
          label: window.i18next.t('pmwjs_flux'),
          value: AiImageModelOption.DeepInfraFluxSchnell.toString(),
          active: model === AiImageModelOption.DeepInfraFluxSchnell,
          description: window.i18next.t('pmwjs_ai_model_flux_desc'),
          topRightIcon: 'icon-credits',
          credits: getCreditCostForModel(AiImageModelOption.DeepInfraFluxSchnell),
          disabled: mediaTypePrompt !== MediaTypePrompt.PHOTO,
        },
      ],
    },
  ];
};
